import React from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

/**
 * Generic ClearButton Component
 * 
 * @param {Object} props
 * @param {Function} props.onClick - Callback when clear button is clicked
 * @param {string} props.variant - Button variant: 'button' | 'icon' (default: 'button')
 * @param {string} props.label - Button label text (default: 'Clear')
 * @param {boolean} props.showTooltip - Show tooltip (default: true)
 * @param {string} props.tooltipTitle - Custom tooltip title
 * @param {boolean} props.disabled - Disable button
 * @param {string} props.color - Button color (default: 'error')
 * @param {Object} props.sx - Additional sx styles
 * @param {Object} props.buttonProps - Additional props to pass to Button/IconButton
 */
export const ClearButton = ({
    onClick,
    variant = 'button',
    label = 'Clear',
    showTooltip = true,
    tooltipTitle,
    disabled = false,
    color = 'error',
    sx,
    size = 'small',
    ...buttonProps
}) => {
    const ButtonComponent = variant === 'icon' ? IconButton : Button;
    const buttonContent = variant === 'icon' ? (
        <ClearIcon />
    ) : (
        <>
            <ClearIcon sx={{ mr: variant === 'button' ? 1 : 0 }} />
            {label}
        </>
    );

    const tooltipText = tooltipTitle || label;
    const isDisabled = disabled;

    const button = (
        <ButtonComponent
            onClick={onClick}
            disabled={isDisabled}
            color={color}
            size={size}
            {...buttonProps}
            sx={{
                ...(buttonProps.sx || {}),
                ...(sx || {}),
            }}
        >
            {buttonContent}
        </ButtonComponent>
    );

    if (showTooltip && !isDisabled) {
        return (
            <Tooltip title={tooltipText}>
                {button}
            </Tooltip>
        );
    }

    return button;
};

